import { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Badge,
  Card,
  SectionHeading,
} from '@/components/ui';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { apiFetch } from '@/lib/api/client';
import { useWallet } from '@/lib/data/wallet';
import { useBookings } from '@/lib/data/bookings';
import { useCompanionServices, bookingTypeLabel, unitLabel } from '@/lib/data/services';
import { supabase } from '@/lib/supabase';
import { usePresence } from '@/lib/data/presence';

/**
 * Companion-side view. Earnings come from `companion_payable` in the ledger —
 * rupees, never coins. Companions are paid a fixed rupee rate per minute,
 * which is why nothing on this screen is denominated in coins.
 * See docs/03-UNIT-ECONOMICS.md §3.
 */
interface ApplicationStatus {
  application: { status: string; applied_at: string | null } | null;
}

interface Shopfront {
  headline: string | null;
  bio: string | null;
  specialties: string[] | null;
  chat_rate_coins: number;
  audio_rate_coins: number;
  video_rate_coins: number;
}

export default function CompanionDashboard() {
  const { profile, session } = useAuth();
  const { wallet } = useWallet();
  const { bookings } = useBookings();
  const { services: myServices } = useCompanionServices(profile?.id);
  const [sharePct, setSharePct] = useState(60);

  // Real rating, from the companion's own row. Zero until reviews exist.
  const [rating, setRating] = useState(0);
  const [ratingCount, setRatingCount] = useState(0);

  useEffect(() => {
    if (!profile) return;
    let active = true;
    void supabase
      .from('companion_profiles')
      .select('rating_avg, rating_count, revenue_share_pct')
      .eq('id', profile.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!active || !data) return;
        setRating(Number(data.rating_avg) || 0);
        setRatingCount(Number(data.rating_count) || 0);
        setSharePct(Number(data.revenue_share_pct) || 60);
      });
    return () => {
      active = false;
    };
  }, [profile]);
  // Real, and persisted. Manual only — nothing sets it back for them, which is
  // why the label below says so rather than implying it is automatic.
  const { isOnline, loading: presenceLoading, error: presenceError, toggle } = usePresence();
  const [application, setApplication] = useState<ApplicationStatus['application']>(null);
  const [shopfront, setShopfront] = useState<Shopfront | null>(null);

  useEffect(() => {
    let active = true;
    void apiFetch<ApplicationStatus>('/api/companion/me')
      .then((r) => {
        if (active) setApplication(r.application);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!profile) return;
    let active = true;
    void supabase
      .from('companion_profiles')
      .select('headline, bio, specialties, chat_rate_coins, audio_rate_coins, video_rate_coins')
      .eq('id', profile.id)
      .maybeSingle()
      .then(({ data }) => {
        if (active) setShopfront(data as Shopfront | null);
      });
    return () => {
      active = false;
    };
  }, [profile]);

  /**
   * A companion with no rates cannot be called, and one with no headline is
   * skipped over in the directory. Both are invisible failures — nothing errors,
   * they simply never get a call — so the dashboard has to say so.
   */
  const missing: string[] = [];
  if (shopfront) {
    const noRates =
      shopfront.chat_rate_coins === 0 &&
      shopfront.audio_rate_coins === 0 &&
      shopfront.video_rate_coins === 0;
    if (noRates) missing.push('a rate for at least one way of talking');
    if (!shopfront.headline?.trim()) missing.push('a one-line introduction');
    if (!shopfront.bio?.trim()) missing.push('a short bio');
    if (!shopfront.specialties?.length) missing.push('what you are good to talk about');
  }

  const name = profile?.display_name?.trim() || 'Companion';
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');

  const joined = application?.applied_at
    ? new Date(application.applied_at).toLocaleDateString('en-IN', {
        month: 'long',
        year: 'numeric',
      })
    : null;

  /*
   * Real figures.
   *
   * These were invented — "Earned today ₹486", "4.9 from 312 rated sessions" —
   * with a comment claiming they were labelled as placeholders. They were not:
   * they rendered exactly like real numbers. A companion seeing an invented
   * balance is a trust problem, and the fix is to show what is true, including
   * when what is true is zero.
   *
   * Earnings accrue as COINS to the companion's own wallet when a booking
   * completes (migration 0018). Withdrawal converts to rupees, and that module
   * does not exist yet — so this reports coins, which is what they actually
   * hold.
   */
  const mine = bookings.filter((b) => b.role === 'companion');
  const waiting = mine.filter((b) => b.status === 'requested').length;
  const done = mine.filter((b) => b.status === 'completed').length;

  const metrics = [
    {
      label: 'Your balance',
      value: `${(wallet?.balanceCoins ?? 0).toLocaleString('en-IN')} coins`,
      sub: done === 0 ? 'Earnings appear here as bookings complete' : `From ${done} completed`,
    },
    {
      label: 'Waiting on you',
      value: String(waiting),
      sub: waiting === 0 ? 'No new requests' : 'Accept or decline in Bookings',
    },
    {
      label: 'Your rating',
      value: ratingCount > 0 ? rating.toFixed(1) : '—',
      sub: ratingCount > 0 ? `From ${ratingCount} rated bookings` : 'No ratings yet',
    },
  ];

  return (
    <div className="flex flex-col gap-14">
      {/* ═══ Header ═════════════════════════════════════════════════════════ */}
      <header className="animate-rise flex flex-wrap items-start justify-between gap-6">
        <div className="flex items-center gap-4">
          <Avatar initials={initials || 'C'} hue={28} size={64} active={isOnline} />
          <div>
            <p className="eyebrow">Companion dashboard</p>
            <h1 className="mt-1 text-3xl font-semibold">{name}</h1>
            <p className="mt-1 text-sm text-[var(--ink-2)]">
              {application?.status === 'approved' ? 'Approved' : (application?.status ?? 'Active')}
              {joined && <> · joined {joined}</>}
              {session?.user.email && (
                <span className="text-[var(--muted)]"> · {session.user.email}</span>
              )}
            </p>
          </div>
        </div>

        <Card
          className="flex items-center gap-4 px-5 py-3.5 transition-colors duration-200"
          style={{
            background: isOnline ? 'var(--online-soft)' : 'var(--surface)',
            borderColor: isOnline ? 'transparent' : 'var(--line)',
          }}
        >
          <div>
            <p
              className="text-sm font-semibold transition-colors duration-200"
              style={{ color: isOnline ? 'var(--online)' : 'var(--ink-2)' }}
            >
              {isOnline ? 'Taking calls' : 'Not taking calls'}
            </p>
            <p className="text-xs text-[var(--muted)]">
              {isOnline ? 'You appear in search results' : 'Hidden from search'}
              {/* Automatic presence is Phase 3 — nothing turns this off when a
                  tab closes or a connection drops, so say so rather than let
                  someone sit "online" all night believing otherwise. */}
              <span className="block">Stays on until you switch it off</span>
              {presenceError && (
                <span className="block" style={{ color: 'var(--danger)' }}>
                  Could not save — {presenceError}
                </span>
              )}
            </p>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={isOnline}
            aria-label="Taking calls"
            disabled={presenceLoading}
            onClick={() => void toggle()}
            className="relative h-8 w-14 shrink-0 rounded-full transition-colors duration-200 disabled:opacity-50"
            style={{ background: isOnline ? 'var(--online)' : 'var(--line)' }}
          >
            <span
              className="absolute top-1 size-6 rounded-full bg-white shadow-md transition-[left] duration-200 ease-out"
              style={{ left: isOnline ? '1.75rem' : '0.25rem' }}
            />
          </button>
        </Card>
      </header>

      {/* ═══ Profile completeness ═══════════════════════════════════════════ */}
      {missing.length > 0 && (
        <Card className="p-6" style={{ borderColor: 'var(--marigold)' }}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold">Your profile is not finished</h2>
              <p className="mt-1.5 text-sm text-[var(--ink-2)]">
                Members can see you in the directory, but they cannot call you yet. Still needed:
              </p>
              <ul className="mt-3 flex flex-col gap-1.5 text-sm text-[var(--ink-2)]">
                {missing.map((m) => (
                  <li key={m} className="flex gap-2">
                    <span style={{ color: 'var(--marigold)' }}>•</span>
                    {m}
                  </li>
                ))}
              </ul>
            </div>
            <Link
              to="/companion/profile"
              className="shrink-0 rounded-xl px-4 py-2.5 text-sm font-medium text-white transition-all duration-150 hover:-translate-y-px"
              style={{ background: 'var(--marigold)' }}
            >
              Finish profile
            </Link>
          </div>
        </Card>
      )}

      {/* ═══ Metrics ════════════════════════════════════════════════════════ */}
      <div
        className="rounded-xl border-l-[3px] px-5 py-4 text-sm"
        style={{ borderColor: 'var(--busy)', background: 'var(--busy-soft)' }}
      >
        <p className="font-semibold" style={{ color: 'var(--busy)' }}>
          Sample figures
        </p>
        <p className="mt-1 text-[var(--ink-2)]">
          Earnings, sessions and ratings below are placeholders. Real numbers start appearing once
          calls and billing are built. Your name, profile and the online switch are real — the
          switch writes to your profile and decides whether you appear in the directory.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-3">
        {metrics.map((m, i) => (
          <Card
            key={m.label}
            className="animate-rise p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[var(--shadow-md)]"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <p className="eyebrow">{m.label}</p>
            <p className="mt-2 font-display text-4xl font-semibold tabular-nums">
              {m.value}
            </p>
            <p className="mt-1.5 text-sm text-[var(--ink-2)]">{m.sub}</p>
          </Card>
        ))}
      </div>

      {/* ═══ Rates ══════════════════════════════════════════════════════════ */}
      {/*
        This showed a fixed rupee rate card — ₹120/min chat, ₹250 voice — and
        told companions "You are paid a fixed rupee rate for every minute you
        deliver, not a share of coins."

        That is the opposite of what was built. Migration 0018 credits a
        companion `reserved_coins * companion_share_pct / 100` — a share of
        coins, snapshotted per booking. Telling providers the wrong basis for
        their own pay is not a placeholder problem.
      */}
      <section>
        <SectionHeading
          eyebrow="Your pricing"
          action={
            <Link
              to="/companion/services"
              className="text-sm font-medium text-[var(--marigold)] hover:underline"
            >
              Manage services
            </Link>
          }
        >
          What you earn
        </SectionHeading>

        <Card className="p-6">
          <p className="text-[var(--ink-2)]">
            You set a price per service and per way of meeting, and keep{' '}
            <strong className="text-[var(--ink)]">{sharePct}%</strong> of what a booking is worth.
            Your share is fixed onto each booking when it is made, so changing your prices later
            never alters what you are owed for work already done.
          </p>

          {myServices.length === 0 ? (
            <p className="mt-4 text-sm text-[var(--ink-2)]">
              You have not listed any services yet — nobody can book you until you do.
            </p>
          ) : (
            <ul className="mt-5 flex flex-col gap-3">
              {myServices.map((sv) => (
                <li key={sv.id} className="flex flex-wrap items-center gap-3">
                  <span className="w-48 shrink-0 font-medium">{sv.categoryName}</span>
                  <span className="flex flex-wrap gap-2">
                    {sv.rates.map((r) => (
                      <span
                        key={r.bookingType}
                        className="rounded-lg px-2.5 py-1 text-sm tabular-nums"
                        style={{ background: 'var(--surface-2)' }}
                      >
                        {bookingTypeLabel(r.bookingType)} {r.priceCoins}
                        {unitLabel(r.unit)}
                      </span>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>

      {/* ═══ Activity ═══════════════════════════════════════════════════════ */}
      {/* Was a table of invented sessions with invented earnings. Real
          bookings now, or an honest empty state. */}
      <section>
        <SectionHeading
          eyebrow="Activity"
          action={
            <Link to="/bookings" className="text-sm font-medium text-[var(--marigold)] hover:underline">
              All bookings
            </Link>
          }
        >
          Recent bookings
        </SectionHeading>

        {mine.length === 0 ? (
          <Card className="px-6 py-12 text-center">
            <p className="font-medium">Nothing yet</p>
            <p className="mx-auto mt-1.5 max-w-sm text-sm text-[var(--ink-2)]">
              {myServices.length === 0
                ? 'List a service so people can find and book you.'
                : 'Your bookings will appear here as they come in.'}
            </p>
            {myServices.length === 0 && (
              <div className="mt-5">
                <Button to="/companion/services">Add a service</Button>
              </div>
            )}
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <ul>
              {mine.slice(0, 6).map((b, i) => (
                <li
                  key={b.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-5 py-4"
                  style={i === 0 ? undefined : { borderTop: '1px solid var(--line-soft)' }}
                >
                  <div className="min-w-0">
                    <p className="font-medium">{b.category_name}</p>
                    <p className="mt-0.5 text-xs text-[var(--muted)]">
                      {bookingTypeLabel(b.booking_type)} ·{' '}
                      {new Date(b.requested_at).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm tabular-nums">
                      {b.status === 'completed'
                        ? `+${Math.floor((b.settled_coins * sharePct) / 100).toLocaleString('en-IN')} coins`
                        : `${b.price_coins} ${b.price_unit === 'per_hour' ? '/hr' : '/min'}`}
                    </span>
                    <Badge tone={b.status === 'completed' ? 'teal' : 'neutral'}>{b.status}</Badge>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>
    </div>
  );
}
